import React, { useRef, useState } from "react";
import { Upload, ShieldCheck, Clock, AlertTriangle, Image as ImageIcon, IdCard, BookOpen, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const MAX_BYTES = 5 * 1024 * 1024; // 5MB raw

const DOC_TYPES = [
  { id: "drivers_license", icon: IdCard,     two_sided: true  },
  { id: "passport",        icon: BookOpen,   two_sided: false },
  { id: "national_id",     icon: CreditCard, two_sided: true  },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * UI block embedded inside WithdrawalProgressModal at step 3.
 * Lets the user upload identity documents (driver's license / passport /
 * national ID card), shows review status, and supports the admin
 * "re-upload" path.
 */
export default function IdVerificationPanel({ txId, idv, onUploaded }) {
  const { t } = useI18n();
  const status = idv?.status || "awaiting_upload";
  const [docType, setDocType] = useState("drivers_license");
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const twoSided = DOC_TYPES.find((d) => d.id === docType)?.two_sided;

  const handlePick = (side, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("wprog.idv.wrong_type"));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("wprog.idv.too_large"));
      return;
    }
    const preview = URL.createObjectURL(file);
    if (side === "front") {
      setFront(file);
      setFrontPreview(preview);
    } else {
      setBack(file);
      setBackPreview(preview);
    }
  };

  const submit = async () => {
    if (!front) {
      toast.error(t("wprog.idv.both_required"));
      return;
    }
    if (twoSided && !back) {
      toast.error(t("wprog.idv.both_required"));
      return;
    }
    setBusy(true);
    try {
      const front_b64 = await fileToBase64(front);
      const back_b64 = twoSided && back ? await fileToBase64(back) : null;
      await api.post(`/withdrawals/${txId}/id-verification`, {
        doc_type: docType,
        front_b64,
        back_b64,
      });
      toast.success(t("wprog.idv.awaiting_review_title"));
      setFront(null);
      setBack(null);
      setFrontPreview(null);
      setBackPreview(null);
      onUploaded && onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  // ------------------ Status banners (review / approved) ------------------
  if (status === "awaiting_review") {
    return (
      <div data-testid="idv-awaiting-review" className="mx-6 my-3 p-4 border border-[#D19B54]/40 bg-[#D19B54]/5">
        <div className="flex items-start gap-3">
          <Clock size={18} className="text-[#D19B54] shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-sm text-[#D19B54]">{t("wprog.idv.awaiting_review_title")}</div>
            <div className="text-xs text-[#A3A9B8] mt-1">{t("wprog.idv.awaiting_review_desc")}</div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "approved") {
    return (
      <div data-testid="idv-approved" className="mx-6 my-3 p-4 border border-[#7C9C69]/40 bg-[#7C9C69]/5">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} className="text-[#7C9C69] shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-sm text-[#7C9C69]">{t("wprog.idv.approved_title")}</div>
            <div className="text-xs text-[#A3A9B8] mt-1">{t("wprog.idv.approved_desc")}</div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------- Upload form (awaiting / retry) --------------------
  const isRetry = status === "rejected_retry";

  const slots = twoSided
    ? [
        { side: "front", file: front, preview: frontPreview, ref: frontRef, label: t("wprog.idv.front") },
        { side: "back",  file: back,  preview: backPreview,  ref: backRef,  label: t("wprog.idv.back")  },
      ]
    : [
        { side: "front", file: front, preview: frontPreview, ref: frontRef, label: t("wprog.idv.passport_page") },
      ];

  return (
    <div data-testid="idv-upload-panel" className="mx-6 my-3 p-4 border border-[#7C5CFF]/30 bg-[#7C5CFF]/[0.04]">
      {isRetry ? (
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle size={18} className="text-[#C75C5C] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-sm text-[#C75C5C]">{t("wprog.idv.retry_title")}</div>
            <div className="text-xs text-[#A3A9B8] mt-1">{t("wprog.idv.retry_desc")}</div>
            {idv?.reject_reason && (
              <div className="text-xs text-[#A3A9B8] mt-2 italic">
                <span className="text-[#6E7587]">{t("wprog.idv.retry_reason")}:</span> {idv.reject_reason}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-4">
          <div className="font-medium text-sm text-[#7C5CFF]">{t("wprog.idv.heading")}</div>
          <div className="text-xs text-[#A3A9B8] mt-1">{t("wprog.idv.desc")}</div>
        </div>
      )}

      {/* Document type tabs */}
      <div className="mb-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587] mb-2">
          {t("wprog.idv.choose_doctype")}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {DOC_TYPES.map(({ id, icon: Icon }) => {
            const active = docType === id;
            return (
              <button
                key={id}
                type="button"
                data-testid={`idv-doctype-${id}`}
                onClick={() => {
                  setDocType(id);
                  // Reset any back-side selection when switching to passport
                  if (id === "passport") {
                    setBack(null);
                    setBackPreview(null);
                  }
                }}
                className={`p-2.5 border text-xs flex flex-col items-center gap-1.5 transition-colors ${
                  active
                    ? "border-[#7C5CFF] bg-[#7C5CFF]/15 text-[#F4F1EB]"
                    : "border-[#2A2E38] text-[#A3A9B8] hover:border-[#7C5CFF]/60 hover:text-[#F4F1EB]"
                }`}
              >
                <Icon size={16} />
                <span className="leading-tight text-center">{t(`wprog.idv.doctype.${id}`)}</span>
              </button>
            );
          })}
        </div>
        {docType === "passport" && (
          <div className="text-[11px] text-[#A3A9B8] mt-2 italic">
            {t("wprog.idv.passport_hint")}
          </div>
        )}
      </div>

      <div className={`grid gap-3 mt-3 ${twoSided ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
        {slots.map(({ side, file, preview, ref, label }) => (
          <div key={side} className="border border-dashed border-[#2A2E38] hover:border-[#7C5CFF]/60 bg-[#0B0D12] p-3 transition-colors">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587] mb-2">{label}</div>
            {preview ? (
              <div className="relative">
                <img src={preview} alt={label} className="w-full h-32 object-cover border border-[#2A2E38]" />
                <div className="text-xs text-[#A3A9B8] mt-2 truncate font-mono">{file.name}</div>
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-[#3D4252]">
                <ImageIcon size={28} />
              </div>
            )}
            <input
              ref={ref}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid={`idv-file-${side}`}
              onChange={(e) => handlePick(side, e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => ref.current?.click()}
              data-testid={`idv-pick-${side}`}
              className="mt-2 w-full text-xs px-3 py-1.5 border border-[#2A2E38] text-[#A3A9B8] hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <Upload size={12} />
              {preview ? t("wprog.idv.replace") : t("wprog.idv.pick")}
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={!front || (twoSided && !back) || busy}
        data-testid="idv-submit"
        className="mt-4 w-full px-4 py-2 bg-[#7C5CFF] text-white text-sm font-medium hover:bg-[#9C82FF] disabled:bg-[#2A2E38] disabled:text-[#6E7587] transition-colors"
      >
        {busy ? t("wprog.idv.submitting") : t("wprog.idv.submit")}
      </button>
    </div>
  );
}
